/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { css, cx } from "@panda/css";
import { UTCard } from "./UTCard.molecule";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";
import { EmblaOptionsType } from "embla-carousel";
import { useCallback, useEffect, useState } from "react";
import { BackgroundContainer } from "@components/elements/atoms";
import { UtCardSectionFragment } from "@cosmos/hygraph-schema";
import { SectionHeading } from "@components/elements/molecules";
import {
	sectionPaddingBlockEndCva,
	sectionPaddingBlockStartCva,
	sectionPaddingInlineCss,
	usePaddingBlockVariant,
} from "@components/utils/section.utils";

// 9/16 ratio set dimensions
export const CARD_HEIGHT = 600;
export const CARD_WIDTH = 337.5;
const CARD_SPACING = 16;
const CARD_OFFSET = 68;

const styles = css({
	display: "flex",
	flexDirection: "column",
});

const coreStyles = css({
	maxWidth: "100%",

	"--card-height": `${CARD_HEIGHT}px`,
	"--slide-spacing": `${CARD_SPACING}px`,
	"--slide-size": `${CARD_WIDTH}px`,
	"--slide-offset": `${CARD_OFFSET}px`,
	"--slide-height": `calc(var(--card-height) + var(--slide-offset))`,
	minHeight: `var(--slide-height)`,

	"& .static__container": {
		justifyContent: "center",
	},
});

const viewport = css({
	overflow: "hidden",
});

const container = css({
	display: "flex",
	alignItems: "flex-start",
	touchAction: "pan-y pinch-zoom",
	marginInlineStart: "calc(var(--slide-spacing) * -1)",
	paddingBlockStart: "eds.dimension.96",
});

const options: EmblaOptionsType = { dragFree: true, loop: true };

export const UTCardsSection = ({ cards, config }: UtCardSectionFragment): React.JSX.Element | null => {
	const [emblaRef] = useEmblaCarousel(options, [
		AutoScroll({ playOnInit: true, startDelay: 1500, speed: 0.5, stopOnInteraction: false }),
	]);
	const [isCarousel, setIsCarousel] = useState(false);

	let validCardsLength = 0;
	if (cards?.length) {
		for (let i = 0; i < cards.length; i++) {
			if (cards[i]?.cardImage?.url) {
				validCardsLength++;
			}
		}
	}

	const checkSize = useCallback((): void => {
		if (validCardsLength) {
			const clientWidth = document.body.clientWidth;
			const cardsWidth = validCardsLength * CARD_WIDTH + (validCardsLength - 1) * CARD_SPACING;

			if (cardsWidth <= clientWidth) {
				setIsCarousel(false);
			} else {
				setIsCarousel(true);
			}
		} else {
			setIsCarousel(false);
		}
	}, [validCardsLength, setIsCarousel]);

	useEffect(() => {
		checkSize();

		window.addEventListener("resize", checkSize);
		return () => {
			window.removeEventListener("resize", checkSize);
		};
	}, [checkSize]);

	const paddingBlockVariant = usePaddingBlockVariant();

	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(validCardsLength);
	if (!validHeading && !validContent) return null;

	const cardsElement = cards.map((card, i) => (
		<UTCard key={i} card={card} alignment={i % 2 === 0 ? "top" : "bottom"} />
	));

	const paddingEndStyles = sectionPaddingBlockEndCva({
		paddingBlockVariant: !!config?.disablePaddingBottom ? "nopadding" : paddingBlockVariant,
	});

	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
		>
			<div
				className={cx(
					"ut-cards-section",
					sectionPaddingBlockStartCva({ paddingBlockVariant }),
					paddingEndStyles,
					styles,
				)}
			>
				{validHeading ? (
					<div className={sectionPaddingInlineCss}>
						<SectionHeading {...config?.heading} />
					</div>
				) : null}
				{validContent ? (
					<div className={cx("ut-cards-core", isCarousel ? "carousel" : "static", coreStyles)}>
						{isCarousel ? (
							<div className={cx("embla__viewport", viewport)} ref={emblaRef}>
								<div className={cx("embla__container", container, paddingEndStyles)}>{cardsElement}</div>
							</div>
						) : (
							<div className={cx("static__container", container, paddingEndStyles)}>{cardsElement}</div>
						)}
					</div>
				) : null}
			</div>
		</BackgroundContainer>
	);
};
